import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Button, EmptyState } from "@nexus/ui";
import { isVendorCapability } from "@nexus/contracts";
import {
  connectionsQueryOptions,
  ordersQueryOptions,
  vendorsQueryOptions,
} from "@/lib/queries";
import { ConnectWizard } from "../ConnectWizard/ConnectWizard";
import { useConnectWizardStore } from "../../store/connectWizard.store";
import {
  emptyVendorCatalogFilters,
  presentVendorCatalog,
  type VendorCatalogFilters,
} from "../../lib/vendor-catalog-model";
import { readRecentVendorIds, rememberRecentVendor } from "../../lib/recent-vendors";
import { VendorCatalogCard, VendorCatalogCardSkeleton } from "./VendorCatalogCard";
import { VendorDetailDrawer } from "./VendorDetailDrawer";
import { VendorsToolbar } from "./VendorsToolbar";
import type { MarketplaceSearch } from "@/routes/_authenticated/marketplace.index";

const SKELETON_COUNT = 8;

export interface VendorsTabProps {
  tenantId: string;
  search: MarketplaceSearch;
  onSearchChange: (patch: Partial<MarketplaceSearch>) => void;
  onSendScripts: (vendorId: string) => void;
}

export function searchToFilters(search: MarketplaceSearch): VendorCatalogFilters {
  const capabilities = parseList(search.capability).filter(isVendorCapability);
  return {
    query: search.q ?? "",
    connection: search.status === "connected" || search.status === "available"
      ? search.status
      : null,
    capabilities,
    turnaround:
      search.turnaround === "fast" ||
      search.turnaround === "standard" ||
      search.turnaround === "longer"
        ? search.turnaround
        : null,
    priceTier:
      search.price === "budget" || search.price === "mid" || search.price === "premium"
        ? search.price
        : null,
    sort:
      search.sort === "fastest" ||
      search.sort === "cheapest" ||
      search.sort === "recent"
        ? search.sort
        : "recommended",
  };
}

export function filtersToSearch(filters: VendorCatalogFilters): Partial<MarketplaceSearch> {
  return {
    q: filters.query.trim() || undefined,
    status: filters.connection ?? undefined,
    capability: filters.capabilities.length > 0 ? filters.capabilities.join(",") : undefined,
    turnaround: filters.turnaround ?? undefined,
    price: filters.priceTier ?? undefined,
    sort: filters.sort === "recommended" ? undefined : filters.sort,
  };
}

function parseList(value: string | undefined): string[] {
  if (!value) return [];
  return value.split(",").map((item) => item.trim()).filter(Boolean);
}

export function VendorsTab({
  tenantId,
  search,
  onSearchChange,
  onSendScripts,
}: VendorsTabProps) {
  const { t } = useTranslation();
  const [recentIds, setRecentIds] = useState(readRecentVendorIds);

  const vendorsQuery = useQuery(vendorsQueryOptions(tenantId));
  const connectionsQuery = useQuery(connectionsQueryOptions(tenantId));
  const ordersQuery = useQuery(
    ordersQueryOptions(tenantId, { limit: 100, offset: 0 }),
  );

  const wizardVendorId = useConnectWizardStore((s) => s.vendorId);
  const wizardOpen = useConnectWizardStore((s) => s.open);
  const openWizard = useConnectWizardStore((s) => s.openWizard);
  const closeWizard = useConnectWizardStore((s) => s.closeWizard);

  const filters = searchToFilters(search);
  const vendors = vendorsQuery.data ?? [];
  const connections = connectionsQuery.data ?? [];
  const orders = ordersQuery.data?.orders ?? [];

  const cards = useMemo(
    () => presentVendorCatalog(vendors, connections, orders, filters, recentIds),
    [vendors, connections, orders, filters, recentIds],
  );

  const selectedCard = useMemo(() => {
    if (!search.vendor) return null;
    const all = presentVendorCatalog(
      vendors,
      connections,
      orders,
      emptyVendorCatalogFilters(),
      recentIds,
    );
    return all.find((card) => card.vendor.id === search.vendor) ?? null;
  }, [search.vendor, vendors, connections, orders, recentIds]);

  const recentOrdersForVendor = useMemo(() => {
    if (!search.vendor) return [];
    return orders
      .filter((order) => order.vendorId === search.vendor)
      .sort((a, b) => (b.sentAt ?? b.createdAt).localeCompare(a.sentAt ?? a.createdAt));
  }, [orders, search.vendor]);

  function openVendor(vendorId: string) {
    setRecentIds(rememberRecentVendor(vendorId));
    onSearchChange({ vendor: vendorId });
  }

  if (vendorsQuery.isLoading || connectionsQuery.isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 min-[1440px]:grid-cols-4">
        {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
          <VendorCatalogCardSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (vendorsQuery.isError) {
    return (
      <div className="py-12 text-center">
        <p className="font-medium">{t("marketplace.catalog.errorTitle", "Could not load partners")}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("errors.tryAgain", "Please try again.")}
        </p>
      </div>
    );
  }

  const hasAnyVendors = vendors.length > 0;
  const hasNonConnectionFilters =
    Boolean(filters.query) ||
    filters.capabilities.length > 0 ||
    filters.turnaround != null ||
    filters.priceTier != null;
  const hasActiveFilters = hasNonConnectionFilters || filters.connection != null;

  return (
    <div className="flex flex-col gap-5">
      <VendorsToolbar
        filters={filters}
        connectedCount={connections.length}
        recentCount={recentIds.length}
        onChange={(next) => onSearchChange(filtersToSearch(next))}
      />

      {!hasAnyVendors ? (
        <EmptyState
          title={t("marketplace.catalog.empty", "No partners available yet.")}
          description={t(
            "marketplace.catalog.emptyDescription",
            "Check back soon — partners are being onboarded.",
          )}
        />
      ) : cards.length === 0 &&
        filters.connection === "connected" &&
        !hasNonConnectionFilters ? (
        // Connected-only empty must win over the generic filter empty: `status=connected`
        // is an active filter, and that copy ("Clear filters") is the wrong CTA here.
        <EmptyState
          icon="link"
          title={t("marketplace.connections.empty", "No connections yet")}
          description={t(
            "marketplace.vendors.emptyConnected.description",
            "Connect to a partner from the catalog to send approved scripts.",
          )}
          action={
            <Button
              type="button"
              onClick={() => onSearchChange({ status: undefined })}
            >
              {t("marketplace.vendors.emptyConnected.action", "Browse partners")}
            </Button>
          }
        />
      ) : cards.length === 0 && hasActiveFilters ? (
        <EmptyState
          title={t("marketplace.vendors.emptyFilters.title", "No partners match these filters")}
          description={t(
            "marketplace.vendors.emptyFilters.description",
            "Clear search or filters to see the full catalog.",
          )}
          action={
            <Button
              type="button"
              variant="outline"
              onClick={() => onSearchChange(filtersToSearch(emptyVendorCatalogFilters()))}
            >
              {t("marketplace.vendors.emptyFilters.clear", "Clear filters")}
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 min-[1440px]:grid-cols-4">
          {cards.map((card) => (
            <VendorCatalogCard
              key={card.vendor.id}
              card={card}
              search={search}
              onConnect={openWizard}
              onManage={openVendor}
              onSendScripts={onSendScripts}
            />
          ))}
        </div>
      )}

      <VendorDetailDrawer
        card={selectedCard}
        tenantId={tenantId}
        recentOrders={recentOrdersForVendor}
        onClose={() => onSearchChange({ vendor: undefined })}
        onConnect={openWizard}
        onSendScripts={onSendScripts}
      />

      {wizardVendorId && (
        <ConnectWizard
          vendorId={wizardVendorId}
          tenantId={tenantId}
          open={wizardOpen}
          onClose={closeWizard}
          onConnectSuccess={(connection) => {
            setRecentIds(rememberRecentVendor(connection.vendorId));
            onSearchChange({ vendor: connection.vendorId, status: undefined });
          }}
        />
      )}
    </div>
  );
}
